import React, {
  useState,
  useEffect,
  useRef,
  ChangeEvent,
  useCallback,
} from 'react'
import { useTranslation } from 'react-i18next'
import { faFileUpload } from '@fortawesome/pro-solid-svg-icons'
import classNames from 'classnames'
import { commmonAllowedFiles } from 'helpers/allowedExtensionFiles.helper'
import { awesomeFileInputValidator } from 'helpers/validators.helper'
import { IAllowedFile } from 'models/allowedFiles.model'
import { gbToBytes } from 'helpers/others.helper'
import EmptyState from 'components/reusables/emptyState'
import FileBlock from './components/FileBlock'
import { FileUpload } from './types'
import styles from './AwesomeFileInput.module.scss'

interface IFileInput {
  id?: string
  files?: FileUpload[]
  sizeLimit?: number
  multiFile: boolean
  allowedFiles?: IAllowedFile[]
  disabled?: boolean
  onRetry?: any
  onCancel?: any
  onDrag?: any
  onDragIn?: any
  onDragOut?: any
  onChange(files: File[]): void
  className?: string
  error?: boolean
}

const ONE_GB = gbToBytes(1)

const AwesomeFileInput: React.FC<IFileInput> = ({
  id,
  disabled = false,
  sizeLimit = ONE_GB,
  files,
  multiFile,
  allowedFiles = commmonAllowedFiles,
  onRetry = undefined,
  onCancel = undefined,
  onDrag = undefined,
  onDragOut = undefined,
  onChange,
  className,
  error,
}) => {
  const { t } = useTranslation()
  const translationPrefix = 'reusables.fileUpload'

  const fileInputRef = useRef<HTMLDivElement>(null)
  const hideInputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const handleDrag = useCallback(
    (e: DragEvent) => {
      const { dataTransfer } = e
      const { files } = { ...dataTransfer }
      e.preventDefault()
      e.stopPropagation()

      !dragging && setDragging(true)
      if (files && files.length > 0) onDrag?.(e)
    },
    [dragging, onDrag]
  )

  const handleDragIn = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragOut = useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setDragging(false)
      onDragOut && onDragOut()
    },
    [onDragOut]
  )

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setDragging(false)

      const files = e.dataTransfer?.files
      if (files && files?.length > 0) {
        const readyToUpload = awesomeFileInputValidator(
          Object.values(files),
          allowedFiles,
          sizeLimit,
          '1 GB',
          multiFile
        )
        onChange(readyToUpload)
      }
    },
    [allowedFiles, multiFile, onChange, sizeLimit]
  )

  useEffect(() => {
    const fileInput = fileInputRef.current
    if (fileInput) {
      fileInput.addEventListener('dragenter', handleDragIn)
      fileInput.addEventListener('dragleave', handleDragOut)
      fileInput.addEventListener('dragover', handleDrag)
      fileInput.addEventListener('drop', handleDrop)
    }
    return () => {
      if (fileInput) {
        fileInput.removeEventListener('dragenter', handleDragIn)
        fileInput.removeEventListener('dragleave', handleDragOut)
        fileInput.removeEventListener('dragover', handleDrag)
        fileInput.removeEventListener('drop', handleDrop)
      }
    }
  }, [handleDrag, handleDragIn, handleDragOut, handleDrop])

  const handleSelectChange = ({
    target: { files },
  }: ChangeEvent<HTMLInputElement>) => {
    if (files && (files?.length > 0 || !disabled)) {
      const readyToUpload = awesomeFileInputValidator(
        Object.values(files),
        allowedFiles,
        sizeLimit,
        '1 GB',
        multiFile
      )
      onChange(readyToUpload)
    }
  }

  const handleClick = () => {
    const hideInput = hideInputRef.current
    if (hideInput) {
      hideInput.value = ''
      hideInput.click()
    }
  }

  const handleDownload = (url: string) => {
    const element = document.createElement('a')
    element.setAttribute('href', url)
    element.setAttribute('target', '_blank')
    element.style.display = 'none'
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  return (
    <>
      <div
        className={classNames(
          'p-0',
          styles.container,
          disabled && styles.disabled,
          dragging && styles.onDrag,
          error && styles.error,
          className
        )}
      >
        <input
          id={id && `contained-button-file-${id}`}
          ref={hideInputRef}
          disabled={disabled}
          name={id}
          className="d-none"
          type="file"
          multiple={multiFile}
          onChange={handleSelectChange}
        />
        <div id={id} ref={fileInputRef} onClick={handleClick}>
          <EmptyState
            title={t(`${translationPrefix}.title`)}
            actionTitle={t(`${translationPrefix}.actionTitle`)}
            icon={faFileUpload}
            actionBackground={false}
            iconSize="small"
          />
        </div>
      </div>
      <div className="fileBlock">
        {!!files?.length && (
          <div className={styles.files}>
            {files.map((file) => (
              <FileBlock
                key={file.id}
                data={file}
                disabled={disabled}
                onDownload={() => {
                  if (file.download) handleDownload(file.download)
                }}
                onRetry={() => onRetry(file.name, file.id)}
                onDelete={() => onCancel(file.token ? file.token : file.id)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default AwesomeFileInput
export * from './types'
